import React, { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Briefcase, CheckCircle2 } from 'lucide-react'
import { motion } from 'framer-motion'

export default function DataTable({ rows, onRowsChange }) {
  const toYm = (row) => {
    const key = String(row?.periodoKey || row?.periodoISO || row?.periodo || '')
    const slash = key.match(/^(\d{2})\/(\d{4})$/)
    if (slash) return Number(`${slash[2]}${slash[1]}`)
    const iso = key.match(/^(\d{4})-(\d{2})/)
    if (iso) return Number(`${iso[1]}${iso[2]}`)
    return 0
  }

  const data = useMemo(() => (Array.isArray(rows) ? rows : []), [rows])
  const [editableRows, setEditableRows] = useState(data)

  useEffect(() => {
    const sorted = [...data].sort((a, b) => toYm(a) - toYm(b))
    setEditableRows(sorted)
  }, [data])

  const handleChange = (index, field, value) => {
    const next = [...editableRows]
    const parsed = field === 'coeficiente' ? Number(value) : Number(value)
    next[index] = {
      ...next[index],
      [field]: Number.isFinite(parsed) ? parsed : 0,
    }
    if (field === 'montoSujetoAAporte' || field === 'coeficiente') {
      const legal = field === 'montoSujetoAAporte' ? (Number.isFinite(parsed) ? parsed : 0) : Number(next[index].montoSujetoAAporte || 0)
      const coef = field === 'coeficiente' ? (Number.isFinite(parsed) ? parsed : 0) : Number(next[index].coeficiente ?? 0)
      next[index].montoActualizado = legal * (coef ?? 0)
    }
    setEditableRows(next)
    onRowsChange?.(next)
  }

  return (
    <div className="table-contrast-grid shadow-[0_20px_50px_rgba(0,0,0,0.14)]">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="border-b border-white/15">
            <tr className="uppercase tracking-wider text-xs font-semibold">
              <th className="px-3 py-2 text-left">Periodo</th>
              <th className="px-3 py-2 text-left">CUIT</th>
              <th className="px-3 py-2 text-left">Sueldo real</th>
              <th className="px-3 py-2 text-left">Sueldo legal</th>
              <th className="px-3 py-2 text-left">Coeficiente</th>
              <th className="px-3 py-2 text-left">Monto actualizado</th>
              <th className="px-3 py-2 text-left">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10 text-sm">
            {editableRows.map((r, i) => (
              <motion.tr
                key={`${r.periodo || r.periodoISO}-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className={`${r.isSuspicious || r.requiresValidation ? 'bg-rose-500/20' : ''}`}
              >
                <td className="px-3 py-2">
                  <div className="inline-flex items-center gap-2">
                    <span>{r.periodo || r.periodoISO}</span>
                    {r.dobleEmpleo && (
                      <span
                        title={(Array.isArray(r.detalleSimultaneidad) ? r.detalleSimultaneidad : [])
                          .map((d) => `${d.empresa || 'S/D'} (${d.cuit || 'S/D'}): $${Number(d.monto || 0).toLocaleString('es-AR')}`)
                          .join(' | ')}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-300/50 bg-amber-500/20 px-2 py-0.5 text-[11px] text-profile-warn"
                      >
                        <Briefcase className="h-3 w-3" /> Doble Empleo
                      </span>
                    )}
                    {r.isEstimatedIndex && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/50 bg-amber-500/20 px-2 py-0.5 text-[11px] text-profile-warn">IPC Estimado</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">{r.cuit || 'S/D'}</td>
                <td className="px-3 py-2">
                  <input type="text" inputMode="decimal" tabIndex={i * 3 + 1} value={Number(r.montoOriginal || r.monto || r.remImpSS || 0)} onChange={(e) => handleChange(i, 'montoOriginal', e.target.value)} className="w-28 rounded border border-white/20 bg-white/10 px-2 py-1 text-xs" />
                </td>
                <td className="text-profile-accent px-3 py-2 font-semibold">
                  <input type="text" inputMode="decimal" tabIndex={i * 3 + 2} value={Number(r.montoSujetoAAporte || 0)} onChange={(e) => handleChange(i, 'montoSujetoAAporte', e.target.value)} className="w-28 rounded border border-white/20 bg-white/10 px-2 py-1 text-xs" />
                </td>
                <td className="px-3 py-2">
                  <input type="number" step="0.000001" tabIndex={i * 3 + 3} value={r.coeficiente ?? 1} onChange={(e) => handleChange(i, 'coeficiente', e.target.value)} className="w-24 rounded border border-white/20 bg-white/10 px-2 py-1 text-xs" />
                </td>
                <td className="text-profile-accent px-3 py-2 font-semibold">{Number(r.montoActualizado || 0).toLocaleString('es-AR')}</td>
                <td className="px-3 py-2">
                  {r.status === 'INFERRED' ? (
                    <span className="panel-profile-warn text-profile-warn inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs" title={r.observacionSanitizador || 'Mes inferido por secuencia faltante'}><AlertTriangle className="h-3 w-3" /> Propuesta</span>
                  ) : r.isSuspicious ? (
                    <span className="panel-profile-danger text-profile-danger inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs" title={r.suspiciousReason || 'Fila sospechosa, no descartada por politica zero-drop'}><AlertTriangle className="h-3 w-3" /> Sospechosa</span>
                  ) : r.invalidoPorCese ? (
                    <span className="panel-profile-danger text-profile-danger inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"><AlertTriangle className="h-3 w-3" /> Fuera de cese</span>
                  ) : r.requiresValidation ? (
                    <span className="panel-profile-danger text-profile-danger inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"><AlertTriangle className="h-3 w-3" /> Validar PDF</span>
                  ) : Number(r.nivelRescate || 1) > 1 ? (
                    <span className="panel-profile-warn text-profile-warn inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"><AlertTriangle className="h-3 w-3" /> Rescate</span>
                  ) : (
                    <span className="panel-profile-ok text-profile-ok inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"><CheckCircle2 className="h-3 w-3" /> Nivel 1</span>
                  )}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
